import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ValidationItems } from '@kanda-libs/ks-component-ts';

import { ApplyFormFieldValues } from 'pages/Apply/context';

export interface FinancialInformationFieldsHook {
  onBlur: () => void;
  validation: ValidationItems;
}

export default function useFinancialInformationFields(): FinancialInformationFieldsHook {
  const { trigger } = useFormContext<ApplyFormFieldValues>();

  const [grossAnnualIncome, additionalEmployment] = useWatch<
    ApplyFormFieldValues,
    [
      'credit.employment_details.gross_annual_income.amount',
      'credit.additional_employment_details',
    ]
  >({
    name: [
      'credit.employment_details.gross_annual_income.amount',
      'credit.additional_employment_details',
    ],
  });

  const hasAdditionalIncomes = useMemo(
    () => Boolean(additionalEmployment) && additionalEmployment?.length !== 0,
    [additionalEmployment],
  );

  const validateHouseholdIncome = useCallback(
    (householdAnnualIncome?: string | number | boolean): boolean => {
      if (typeof householdAnnualIncome !== 'number') return false;
      if (!additionalEmployment || additionalEmployment?.length === 0)
        return Boolean(
          householdAnnualIncome &&
            grossAnnualIncome &&
            householdAnnualIncome >= grossAnnualIncome,
        );
      const additionalGrossIncomes = additionalEmployment
        ?.map((employment) => employment?.gross_annual_income?.amount || 0)
        ?.reduce((sum, current) => sum + current, 0);
      return Boolean(
        additionalGrossIncomes &&
          householdAnnualIncome &&
          grossAnnualIncome &&
          householdAnnualIncome >= grossAnnualIncome + additionalGrossIncomes,
      );
    },
    [grossAnnualIncome, additionalEmployment],
  );

  const validation: ValidationItems = useMemo(
    () => ({
      min: { value: 1000000, message: 'Amount must be greater than £10,000' },
      required: {
        value: true,
        message: 'Household annual income is required',
      },
      validate: {
        value: (value) => validateHouseholdIncome(value),
        message: `Household annual income cannot be lower than your gross annual ${
          hasAdditionalIncomes ? 'incomes combined.' : 'income.'
        }`,
      },
    }),
    [validateHouseholdIncome, hasAdditionalIncomes],
  );

  const onBlur = useCallback(
    () => trigger('credit.employment_details.household_annual_income'),
    [trigger],
  );

  return {
    validation,
    onBlur,
  };
}

// import { useCallback } from 'react';
// import { useWatch, ValidationItems } from '@kanda-libs/ks-component-ts';

// import { GROSS_INCOME_NAME, ADDITIONAL_EMPLOYMENT_NAME } from './constants';

// export default function useIncomeValidation(): ValidationItems {
//   const grossAnnualIncome = useWatch({ name: GROSS_INCOME_NAME });
//   const additionalEmployment = useWatch({
//     name: ADDITIONAL_EMPLOYMENT_NAME,
//   });

//   const validateHouseholdIncome = useCallback(
//     (householdAnnualIncome) => {
//       if (!additionalEmployment || additionalEmployment?.length === 0)
//         return (
//           householdAnnualIncome &&
//           grossAnnualIncome &&
//           householdAnnualIncome >= grossAnnualIncome
//         );
//       const additionalGrossIncomes = additionalEmployment
//         ?.map((employment) => employment?.gross_annual_income?.amount || 0)
//         ?.reduce((sum, current) => sum + current, 0);
//       return (
//         additionalGrossIncomes &&
//         householdAnnualIncome &&
//         grossAnnualIncome &&
//         householdAnnualIncome >= grossAnnualIncome + additionalGrossIncomes
//       );
//     },
//     [grossAnnualIncome, additionalEmployment],
//   );

//   return {
//     min: { value: 1000000, message: 'Amount must be greater than £10,000' },
//     required: {
//       value: true,
//       message: 'Household annual income is required',
//     },
//     validate: {
//       value: (value) => validateHouseholdIncome(value),
//       message:
//         'Household annual income cannot be lower than your gross annual income',
//     },
//   };
// }
